import { IBetItemData } from "@finbackoffice/fe-core";
import {
    createContext,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { IBonusRange } from "@finbackoffice/clientbff-client";
import { TicketType } from "@finbackoffice/enums";
import { MultiBetBonusErrorKeys, isMinOddsError, isCategoryError } from "@finbackoffice/site-core";
import { BetSlipContextV2, BetSlipItemsContext } from "./bet-slip-v2.provider";
import { ConfigContextV2 } from "./config-v2.provider";

type IMultiBetBonusContext = {
    currentRange: IBonusRange | null;
    upcomingRange: IBonusRange | null;
    validation: {
        error: MultiBetBonusErrorKeys | "";
        valid: boolean;
    };
    performMultiBetBonusValidate: (betItems: IBetItemData[]) => void;
    possibleBonusAmount: number;
};

type IMultiBetBonusValidation = {
    error: MultiBetBonusErrorKeys | "";
    valid: boolean;
};

export const MultiBetBonusContextV2 = createContext<IMultiBetBonusContext>(null as any);

export const MultiBetBonusProviderV2: FC<PropsWithChildren> = ({ children }) => {
    const { possibleWinAmount, betType, multipleBetStake } = useContext(BetSlipContextV2);
    const { betItems } = useContext(BetSlipItemsContext);

    const {
        sportMultiBetBonusRanges,
        sportMultiBetMinOdds,
        sportMultiBetLimits,
        sportMultiBetCategories,
    } = useContext(ConfigContextV2);
    const [validation, setValidation] = useState<IMultiBetBonusValidation>({
        error: "",
        valid: true,
    });

    const betItemsAmount = betItems.length;
    const minRangeStart = useMemo(
        () => Math.min(...sportMultiBetBonusRanges.map((r) => r.range_start)),
        [sportMultiBetBonusRanges],
    );
    const currentRange = useMemo(() => {
        const lastRange = sportMultiBetBonusRanges[sportMultiBetBonusRanges.length - 1];

        if (validation.error) {
            return null;
        }

        if (betItemsAmount >= lastRange.range_end) {
            return lastRange;
        }

        return (
            sportMultiBetBonusRanges.find(
                (range) => betItemsAmount >= range.range_start && betItemsAmount < range.range_end,
            ) || null
        );
    }, [betItemsAmount, sportMultiBetBonusRanges, validation.error]);

    const upcomingRange = useMemo(() => {
        let result;

        if (currentRange) {
            result = sportMultiBetBonusRanges.find(
                (range) =>
                    range.range_start > currentRange.range_start &&
                    betItemsAmount < range.range_end,
            );
        } else {
            result = sportMultiBetBonusRanges.find((range) => range.range_start === minRangeStart);
        }

        return result || null;
    }, [betItemsAmount, currentRange, minRangeStart, sportMultiBetBonusRanges]);

    const possibleBonusAmount = useMemo(() => {
        let bonus = 0;
        switch (betType) {
            case TicketType.Express: {
                const amount =
                    possibleWinAmount - (multipleBetStake ? parseFloat(multipleBetStake) : 0);

                bonus = currentRange ? (amount * currentRange.rate) / 100 : 0;
                break;
            }
            default:
                break;
        }

        if (sportMultiBetLimits?.max_win) {
            const multibetMaxWin = parseFloat(sportMultiBetLimits.max_win);
            if (bonus >= multibetMaxWin) {
                return multibetMaxWin;
            }
        }

        return bonus;
    }, [betType, currentRange, multipleBetStake, possibleWinAmount, sportMultiBetLimits?.max_win]);

    const performMultiBetBonusValidate = useCallback(
        (betItems: IBetItemData[]) => {
            let error: MultiBetBonusErrorKeys | "" = "";
            if (isMinOddsError(betItems, parseFloat(sportMultiBetMinOdds))) {
                error = "min_odds";
            } else if (isCategoryError(betItems, sportMultiBetCategories)) {
                error = "categories";
            }

            setValidation({ error, valid: !error });
        },
        [sportMultiBetCategories, sportMultiBetMinOdds],
    );

    const value = useMemo(
        () => ({
            validation,
            upcomingRange,
            performMultiBetBonusValidate,
            currentRange,
            possibleBonusAmount,
        }),
        [
            currentRange,
            validation,
            performMultiBetBonusValidate,
            upcomingRange,
            possibleBonusAmount,
        ],
    );
    return (
        <MultiBetBonusContextV2.Provider value={value}>{children}</MultiBetBonusContextV2.Provider>
    );
};
